import { NativeAdapter, FONTS_FOLDER_HANDLE_KEY } from '../../file-system-adapter';
import { Logger } from '../../logger';
import { dispatchManagedFileChanged } from '../../managed-file-events';
import { FilesystemPlugin } from '../filesystem-plugin';
const console = new Logger('FilesystemPlugins');
/**
 * Disk Plugin - uses File System Access API for direct disk access
 */
export class DiskPlugin extends FilesystemPlugin {
    constructor() {
        const adapter = new NativeAdapter(FONTS_FOLDER_HANDLE_KEY);
        super(adapter);
        this.fileSystemObserver = null;
        this.observerSupported = 'FileSystemObserver' in window;
        this.nativeAdapter = adapter;
    }
    getId() {
        return 'disk';
    }
    getName() {
        return 'Disk';
    }
    getIcon() {
        return '<span class="material-symbols-outlined">hard_drive</span>';
    }
    requiresPermission() {
        return true;
    }
    showsManualRefreshButton() {
        return !this.observerSupported;
    }
    async onActivate() {
        const isReady = await this.isReady();
        if (!isReady) {
            return false;
        }
        const hasPermission = await this.nativeAdapter.checkPermission();
        if (!hasPermission) {
            return false;
        }
        await this.setupFileSystemObserver();
        return true;
    }
    async onDeactivate() {
        this.disconnectObserver();
    }
    async setupFileSystemObserver() {
        this.disconnectObserver();
        if (!this.observerSupported) {
            console.log('[DiskPlugin] FileSystemObserver not supported, changes require manual refresh');
            return;
        }
        try {
            const rootHandle = await this.nativeAdapter.getHandleAtPath('/');
            if (!rootHandle || rootHandle.kind !== 'directory') {
                console.log('[DiskPlugin] Cannot get root directory handle for observer');
                return;
            }
            const FileSystemObserver = window.FileSystemObserver;
            this.fileSystemObserver = new FileSystemObserver(async (records) => {
                console.log('[DiskPlugin] FileSystemObserver detected changes:', records.length);
                window.dispatchEvent(new CustomEvent('diskFilesChanged', {
                    detail: { records }
                }));
                dispatchManagedFileChanged({
                    pluginId: this.getId(),
                    source: 'file-system-observer',
                    records,
                    internalWrite: false
                });
            });
            await this.fileSystemObserver.observe(rootHandle, {
                recursive: true
            });
            console.log('[DiskPlugin] FileSystemObserver watching root directory');
        }
        catch (error) {
            console.error('[DiskPlugin] Failed to set up FileSystemObserver:', error);
        }
    }
    disconnectObserver() {
        if (this.fileSystemObserver) {
            try {
                this.fileSystemObserver.disconnect();
            }
            catch (e) {
                // Ignore disconnect errors
            }
            this.fileSystemObserver = null;
        }
    }
    async showSetupUI(options) {
        try {
            const selected = await this.nativeAdapter.selectDirectory(options);
            if (!selected) {
                return false;
            }
            this.redrawTitleBarButtons();
            await this.setupFileSystemObserver();
            return true;
        }
        catch (error) {
            console.error('[DiskPlugin] Setup cancelled or failed:', error);
            return false;
        }
    }
    async isReady() {
        return await this.nativeAdapter.hasDirectory();
    }
    getDefaultPath() {
        return '/';
    }
    supportsUpload() {
        return false;
    }
    getDirectoryName() {
        return this.nativeAdapter.getDirectoryName();
    }
    async requestPermission() {
        const permission = await this.nativeAdapter.requestPermission();
        return permission === 'granted';
    }
    canClose() {
        return true;
    }
    async close() {
        this.disconnectObserver();
        await this.nativeAdapter.clearDirectory();
        console.log('[DiskPlugin]', 'Folder access closed');
    }
    getTitleBarMenuItems() {
        return [
            {
                label: 'Close Folder Access',
                icon: 'close',
                action: async () => {
                    await this.close();
                    window.dispatchEvent(new CustomEvent('pluginFolderClosed'));
                }
            }
        ];
    }
    async updateUI(uiCallbacks) {
        if (this._unsupported) {
            uiCallbacks.showUnsupportedBrowserUI();
            uiCallbacks.showPermissionBanner(false);
            return;
        }
        uiCallbacks.hideUnsupportedBrowserUI();
        const isReady = await this.isReady();
        if (!isReady) {
            uiCallbacks.showOpenFolderUI();
            uiCallbacks.showPermissionBanner(false);
        }
        else {
            uiCallbacks.hideOpenFolderUI();
            const permission = await this.nativeAdapter.checkPermission();
            if (permission !== 'granted') {
                uiCallbacks.showPermissionBanner(true);
            }
            else {
                uiCallbacks.showPermissionBanner(false);
            }
        }
    }
    async clearDirectory() {
        await this.nativeAdapter.clearDirectory();
    }
    supportsOpenFilePicker() {
        return this.nativeAdapter.hasDirectory();
    }
    supportsSaveAsFilePicker() {
        return this.nativeAdapter.hasDirectory();
    }
    async showOpenFilePicker(options) {
        if (!this.nativeAdapter.hasDirectory()) {
            return null;
        }
        try {
            const pickerOptions = {
                multiple: false
            };
            if (options?.types) {
                pickerOptions.types = options.types;
            }
            const startPath = options?.startIn || '/';
            const startHandle = await this.nativeAdapter.getHandleAtPath(startPath);
            if (startHandle) {
                pickerOptions.startIn = startHandle;
            }
            const [fileHandle] = await window.showOpenFilePicker(pickerOptions);
            const relativePath = await this.getRelativePath(fileHandle);
            return relativePath;
        }
        catch (error) {
            if (error.name === 'AbortError') {
                console.log('[DiskPlugin] Open file picker cancelled');
                return null;
            }
            console.error('[DiskPlugin] Error opening file picker:', error);
            return null;
        }
    }
    async showSaveFilePicker(options) {
        if (!this.nativeAdapter.hasDirectory()) {
            return null;
        }
        try {
            const pickerOptions = {};
            if (options?.suggestedName) {
                pickerOptions.suggestedName = options.suggestedName;
            }
            if (options?.types) {
                pickerOptions.types = options.types;
            }
            const startPath = options?.startIn || '/';
            const startHandle = await this.nativeAdapter.getHandleAtPath(startPath);
            if (startHandle) {
                pickerOptions.startIn = startHandle;
            }
            const fileHandle = await window.showSaveFilePicker(pickerOptions);
            const relativePath = await this.getRelativePath(fileHandle);
            return relativePath;
        }
        catch (error) {
            if (error.name === 'AbortError') {
                console.log('[DiskPlugin] Save file picker cancelled');
                return null;
            }
            console.error('[DiskPlugin] Error saving file picker:', error);
            return null;
        }
    }
    async getRelativePath(fileHandle) {
        try {
            const rootHandle = await this.nativeAdapter.getHandleAtPath('/');
            if (!rootHandle || rootHandle.kind !== 'directory') {
                return null;
            }
            const pathParts = await rootHandle.resolve(fileHandle);
            if (pathParts === null) {
                console.warn('[DiskPlugin] Selected file is outside the root directory');
                return null;
            }
            return '/' + pathParts.join('/');
        }
        catch (error) {
            console.error('[DiskPlugin] Error resolving path:', error);
            return null;
        }
    }
}
